import type { DeviceBreakdown } from "../api";

type Props = {
  devices: DeviceBreakdown;
};

function pct(part: number, total: number): string {
  if (total <= 0) return "—";
  return `${((part / total) * 100).toFixed(2)} %`;
}

export function DevicesCard({ devices }: Props) {
  const total =
    devices.desktop + devices.mobile + devices.tablet + devices.unknown;

  // Render all four buckets including Unknown -- otherwise the visible
  // percentages don't sum to 100% when the User-Agent header is stripped
  // upstream and sessions fall into the unknown bucket.
  const cells: { label: string; value: number; icon: JSX.Element }[] = [
    {
      label: "Desktop",
      value: devices.desktop,
      icon: <DesktopIcon />,
    },
    {
      label: "Mobile",
      value: devices.mobile,
      icon: <MobileIcon />,
    },
    {
      label: "Tablet",
      value: devices.tablet,
      icon: <TabletIcon />,
    },
    {
      label: "Unknown",
      value: devices.unknown,
      icon: <UnknownIcon />,
    },
  ];

  return (
    <div className="rounded-2xl border border-mars-line bg-mars-charcoal/40 p-5">
      <div className="eyebrow">Visitors by device</div>
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        {cells.map((c) => (
          <div key={c.label} className="flex flex-col gap-2">
            <div className="text-mars-white/60">{c.icon}</div>
            <div className="mono text-[0.65rem] uppercase tracking-[0.16em] text-mars-white/60">
              {c.label}
            </div>
            <div className="mono text-xl font-bold tabular-nums text-mars-white">
              {pct(c.value, total)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DesktopIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="12" rx="1.5" />
      <path d="M9 20h6M12 16v4" />
    </svg>
  );
}

function MobileIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <rect x="7" y="3" width="10" height="18" rx="2" />
      <path d="M11 17.5h2" />
    </svg>
  );
}

function TabletIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M11 18h2" />
    </svg>
  );
}

function UnknownIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .9-1 1.7M12 17h.01" />
    </svg>
  );
}
